import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { Card } from '../../components/Card';
import { CmsEditorLayout } from '../../components/CmsEditorLayout';
import { useAdminCms, saveCmsSection, clearCmsSection, type AdminCmsSection } from '../../api/cms';
import { CMS_REGISTRY } from '../../content/registry';
import { LIST_FALLBACKS } from '../../content/fallbacks';

/**
 * Marketing CMS editor (platform admin) — §WS5b, extended to full coverage.
 * Registry-driven (`content/registry.ts`); layout is the shared two-pane
 * CmsEditorLayout (page rail + collapsed accordion sections) instead of one
 * endless scroll. An unsaved section shows the built-in fallback copy (from
 * `content/fallbacks.ts`) with a "Using default copy" badge; once saved it
 * shows "Customized" + a "Reset to default" button. Saving/resetting
 * invalidates the public `cms` query so the live marketing pages pick up the
 * change immediately. No raw HTML, no page builder.
 */
export function Cms() {
  const { data: sections = [], isLoading } = useAdminCms();
  const qc = useQueryClient();
  const byKey = new Map<string, AdminCmsSection>(sections.map((s) => [s.sectionKey, s]));

  async function onSaved() {
    await qc.invalidateQueries({ queryKey: ['admin-cms'] });
    await qc.invalidateQueries({ queryKey: ['cms'] });
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-5xl mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Marketing content"
        subtitle="Edit every public marketing page (itcitysoft.com / possignin.com / pricing / features / about / blog), plus the shared default template resellers inherit before their own overrides. Changes go live immediately."
      />
      {isLoading ? (
        <Card>
          <div className="p-6 space-y-3" aria-busy="true">
            <div className="skeleton h-5 w-40 rounded" />
            <div className="skeleton h-24 rounded" />
            <div className="skeleton h-24 rounded" />
          </div>
        </Card>
      ) : (
        <CmsEditorLayout
          pages={CMS_REGISTRY}
          byKey={byKey}
          fallbackListFor={(key) => LIST_FALLBACKS[key] ?? []}
          save={saveCmsSection}
          clear={clearCmsSection}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
